import React, { useState } from "react";
import { toast } from "sonner";
import { Database, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { ACTION_LABEL } from "@/lib/rights";
import { EvidenceUploadButton } from "@/components/rights/EvidenceUploadButton";

const Mini = ({ value, onChange, options, placeholder, testId, disabled, width = "w-[150px]" }) => (
  <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className={`h-8 ${width} text-[11px] bg-white`} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const CONF_DOT = { PENDING: "bg-amber-400", DATA_FOUND: "bg-[#008080]", NO_DATA: "bg-slate-300" };

export const SystemRow = ({ r, s, meta, users, canEdit, onChanged, onRemove }) => {
  const [retainDraft, setRetainDraft] = useState(s.decision === "RETAIN" || false);
  const [note, setNote] = useState(s.retain_note || "");
  const isDeletion = r.request_type === "DELETION";
  const found = s.confirmation === "DATA_FOUND";
  const needsAction = found && (!isDeletion || s.decision === "DELETE");
  const ro = !canEdit || r.status === "CLOSED";

  const patch = async (body, msg) => {
    try {
      await api.patch(`/rights/${r.id}/systems/${s.id}`, body);
      if (msg) toast.success(msg);
      onChanged();
    } catch (e) { toast.error(apiError(e, "Update failed")); }
  };
  const onDecision = (v) => {
    if (v === "DELETE") { setRetainDraft(false); patch({ decision: "DELETE" }, "Marked for deletion"); } else setRetainDraft(true);
  };
  const onReason = (v) => {
    if (v === "OTHER" && !note.trim()) { toast.info("Add a note describing the reason, then save"); setRetainDraft("OTHER"); return; }
    patch({ decision: "RETAIN", retain_reason: v, retain_note: note }, "Retention recorded");
  };

  return (
    <div className="py-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto] gap-3" data-testid={`system-row-${s.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`w-2 h-2 rounded-full ${CONF_DOT[s.confirmation]}`} />
          <Database className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-sm font-semibold text-[#003366]" data-testid={`system-name-${s.id}`}>{s.system_name}</span>
          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${s.source === "ROPA" ? "bg-blue-50 text-[#008080]" : "bg-slate-100 text-slate-600"}`}>
            {s.source === "ROPA" ? "RoPA" : "Manual"}
          </span>
          <span className="text-[11px] text-slate-500">{s.asset_type}{s.hosting && ` · ${s.hosting}`}{s.no_linked_system && " · no system linked in RoPA"}</span>
        </div>
        {s.activities?.length > 0 && (
          <div className="mt-1.5 text-[11px] text-slate-600 space-y-0.5">
            {s.activities.map((a) => (
              <div key={a.id}>
                <span className="font-medium">{a.name}</span>
                {a.retention_basis && <span className="text-slate-500"> · retain {a.retention_months != null ? `${a.retention_months} months · ` : ""}{a.retention_basis}</span>}
              </div>
            ))}
          </div>
        )}
        {s.data_elements?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {s.data_elements.slice(0, 10).map((d) => <span key={d} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{d}</span>)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap xl:justify-end">
        <Mini value={s.system_owner} onChange={(v) => patch({ system_owner: v }, `Owner set · task sent to ${v}`)} disabled={ro}
              placeholder="System owner" options={users.map((u) => [u.username, u.full_name || u.username])} testId={`system-owner-${s.id}`} />
        <Mini value={s.confirmation} onChange={(v) => patch({ confirmation: v })} disabled={ro} width="w-[130px]"
              options={Object.entries(meta.confirmations)} testId={`system-confirmation-${s.id}`} />
        {isDeletion && found && (
          <Mini value={retainDraft ? "RETAIN" : s.decision} onChange={onDecision} disabled={ro} width="w-[110px]" placeholder="Decision"
                options={[["DELETE", "Delete"], ["RETAIN", "Retain"]]} testId={`system-decision-${s.id}`} />
        )}
        {isDeletion && found && retainDraft && (
          <>
            <Mini value={retainDraft === "OTHER" ? "OTHER" : s.retain_reason} onChange={onReason} disabled={ro} placeholder="Retention reason"
                  options={Object.entries(meta.retain_reasons)} testId={`system-retain-reason-${s.id}`} />
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" disabled={ro}
                   className="h-8 w-[140px] text-[11px]" data-testid={`system-retain-note-${s.id}`} />
            {!ro && (
              <Button size="sm" variant="outline" className="h-8 text-[11px]" data-testid={`system-retain-save-${s.id}`}
                      onClick={() => patch({ decision: "RETAIN", retain_reason: retainDraft === "OTHER" ? "OTHER" : s.retain_reason, retain_note: note }, "Retention saved")}>
                Save
              </Button>
            )}
          </>
        )}
        {needsAction && (
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700 px-2 h-8 border border-slate-200 rounded-md bg-white">
            <Checkbox checked={s.action_status === "DONE"} disabled={ro} data-testid={`system-action-done-${s.id}`}
                      onCheckedChange={(c) => patch({ action_status: c ? "DONE" : "PENDING" })} />
            {ACTION_LABEL[r.request_type]}
          </label>
        )}
        <span className="text-[10px] font-mono text-slate-400">{s.evidence_file_ids?.length || 0} file(s)</span>
        {!ro && <EvidenceUploadButton requestId={r.id} category="SYSTEM_OWNER" systemActionId={s.id} onUploaded={onChanged}
                                      label="Owner evidence" testId={`system-attach-${s.id}`} />}
        {!ro && (
          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => onRemove(s)}
                  data-testid={`system-remove-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        )}
      </div>
    </div>
  );
};
